import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { accountRecoveryApi } from '@/api/auth'
import { useTenantStore } from '@/store/tenantStore'
import { getTenantLogoUrl, getTenantId } from '@/utils/tenant'

type Step = 'email' | 'code' | 'password' | 'done'

export default function ResetPasswordPage() {
  const navigate = useNavigate()
  const { tenantInfo } = useTenantStore()
  const { t } = useTranslation('auth')
  const tenantId = getTenantId()
  const logoUrl = getTenantLogoUrl(tenantId, tenantInfo?.logoUrl)

  const [step, setStep] = useState<Step>('email')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [resetToken, setResetToken] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const primaryColor = tenantInfo?.primaryColor || undefined

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      await accountRecoveryApi.requestPasswordReset(email)
      setStep('code')
    } catch (err: any) {
      setError(err.response?.data?.error?.message || t('resetPassword.requestFailed'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      const response = await accountRecoveryApi.verifyCode(email, code)
      setResetToken(response.data.data!.resetToken)
      setStep('password')
    } catch (err: any) {
      setError(err.response?.data?.error?.message || t('resetPassword.invalidCode'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (newPassword !== confirmPassword) {
      setError(t('resetPassword.passwordMismatch'))
      return
    }

    if (newPassword.length < 8) {
      setError(t('resetPassword.passwordMinLength'))
      return
    }

    setIsLoading(true)
    try {
      await accountRecoveryApi.resetPassword(resetToken, newPassword)
      setStep('done')
    } catch (err: any) {
      setError(err.response?.data?.error?.message || t('resetPassword.changeFailed'))
    } finally {
      setIsLoading(false)
    }
  }

  const stepTitles: Record<Step, { title: string; desc: string }> = {
    email: { title: t('resetPassword.title'), desc: t('resetPassword.description') },
    code: { title: t('resetPassword.codeTitle'), desc: t('resetPassword.codeDescription', { email }) },
    password: { title: t('resetPassword.newPasswordTitle'), desc: t('resetPassword.newPasswordDescription') },
    done: { title: t('resetPassword.doneTitle'), desc: t('resetPassword.doneDescription') },
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          {logoUrl ? (
            <img src={logoUrl} alt={tenantInfo?.name || t('resetPassword.logo')} className="mx-auto h-16 w-auto" />
          ) : (
            <div
              className="mx-auto w-16 h-16 rounded-xl flex items-center justify-center text-white text-2xl font-bold"
              style={{ backgroundColor: primaryColor || '#6366f1' }}
            >
              {tenantInfo?.name?.charAt(0) || 'Q'}
            </div>
          )}
          <h2 className="mt-6 text-2xl font-bold text-gray-900">{stepTitles[step].title}</h2>
          <p className="mt-2 text-sm text-gray-600">{stepTitles[step].desc}</p>
        </div>

        {/* Step Progress */}
        {step !== 'done' && (
          <div className="flex items-center justify-center gap-2">
            {(['email', 'code', 'password'] as Step[]).map((s, i) => (
              <div key={s} className="flex items-center gap-2">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step === s
                      ? 'bg-primary-600 text-white'
                      : ['email', 'code', 'password'].indexOf(step) > i
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 text-gray-500'
                  }`}
                  style={step === s ? { backgroundColor: primaryColor } : undefined}
                >
                  {['email', 'code', 'password'].indexOf(step) > i ? '✓' : i + 1}
                </div>
                {i < 2 && <div className="w-8 h-0.5 bg-gray-200" />}
              </div>
            ))}
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Step 1: Email */}
        {step === 'email' && (
          <form className="space-y-6" onSubmit={handleRequestCode}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">{t('resetPassword.email')}</label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="name@company.com"
              />
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 px-4 rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
              style={{ backgroundColor: primaryColor }}
            >
              {isLoading ? t('resetPassword.sending') : t('resetPassword.sendCode')}
            </button>
          </form>
        )}

        {/* Step 2: Code */}
        {step === 'code' && (
          <form className="space-y-6" onSubmit={handleVerifyCode}>
            <div>
              <label htmlFor="code" className="block text-sm font-medium text-gray-700">{t('resetPassword.codeLabel')}</label>
              <input
                id="code"
                type="text"
                required
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                className="mt-1 block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-center text-2xl tracking-[0.5em] font-mono"
                placeholder="000000"
              />
              <p className="mt-2 text-xs text-gray-500">{t('resetPassword.codeExpiry')}</p>
            </div>
            <button
              type="submit"
              disabled={isLoading || code.length !== 6}
              className="w-full py-3 px-4 rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
              style={{ backgroundColor: primaryColor }}
            >
              {isLoading ? t('resetPassword.verifying') : t('resetPassword.verifyCode')}
            </button>
            <button
              type="button"
              onClick={() => { setStep('email'); setCode(''); setError(''); }}
              className="w-full py-2 text-sm text-gray-500 hover:text-gray-700"
            >
              {t('resetPassword.reenterEmail')}
            </button>
          </form>
        )}

        {/* Step 3: New Password */}
        {step === 'password' && (
          <form className="space-y-6" onSubmit={handleResetPassword}>
            <div className="space-y-4">
              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">{t('resetPassword.newPassword')}</label>
                <input
                  id="newPassword"
                  type="password"
                  required
                  minLength={8}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="mt-1 block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder={t('resetPassword.newPasswordPlaceholder')}
                />
              </div>
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">{t('resetPassword.confirmPassword')}</label>
                <input
                  id="confirmPassword"
                  type="password"
                  required
                  minLength={8}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="mt-1 block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder={t('resetPassword.confirmPasswordPlaceholder')}
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 px-4 rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
              style={{ backgroundColor: primaryColor }}
            >
              {isLoading ? t('resetPassword.changing') : t('resetPassword.changePassword')}
            </button>
          </form>
        )}

        {/* Step 4: Done */}
        {step === 'done' && (
          <div className="bg-white rounded-lg shadow p-8 text-center space-y-6">
            <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-gray-600">{t('resetPassword.doneMessage')}</p>
            <button
              onClick={() => navigate('/login')}
              className="w-full py-3 px-4 rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
              style={{ backgroundColor: primaryColor }}
            >
              {t('resetPassword.goToLogin')}
            </button>
          </div>
        )}

        {/* Bottom links */}
        {step !== 'done' && (
          <div className="flex justify-center gap-4 text-sm">
            <Link to="/login" className="text-gray-500 hover:text-gray-700">{t('resetPassword.backToLogin')}</Link>
            <span className="text-gray-300">|</span>
            <Link to="/find-account" className="text-gray-500 hover:text-gray-700">{t('resetPassword.findAccount')}</Link>
          </div>
        )}
      </div>
    </div>
  )
}
